import "~/types/google.d.ts"
import { useEffect, useRef } from "react"
import { Input } from "./input"

interface PlacesAutocompleteProps {
  onLocationSelect: (
    latitude: number,
    longitude: number,
    addressData: {
      name?: string
      streetAddress: string
      streetAddress2: string | null
      city: string | null
      stateProvince: string | null
      postalCode: string | null
      country: string | null
      formattedAddress: string
      placeId: string
      administrativeAreaLevel1: string | null
      administrativeAreaLevel2: string | null
      neighborhood: string | null
      websiteUrl?: string
      googleMapsUrl?: string
      phone?: string
      timeZone: string | null
    }
  ) => void
  className?: string
  includeBusinesses?: boolean
}

// Add interface for address components
interface AddressComponent {
  long_name: string
  short_name: string
  types: string[]
}

// Define PlaceResult type
interface ExtendedPlaceResult {
  address_components?: AddressComponent[]
  formatted_address?: string
  place_id?: string
  name?: string
  geometry?: {
    location: {
      lat: () => number
      lng: () => number
    }
  }
  website?: string
  url?: string
  international_phone_number?: string
}

export function PlacesAutocomplete({
  onLocationSelect,
  className,
  includeBusinesses = false,
}: PlacesAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)

  useEffect(() => {
    if (!inputRef.current) return

    // Initialize Google Places Autocomplete with all needed fields
    autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
      fields: [
        "geometry",
        "formatted_address",
        "place_id",
        "address_components",
        "name",
        "website",
        "url",
        "international_phone_number",
      ],
      types: includeBusinesses ? ["establishment"] : ["address"],
    })

    // Add listener for place selection
    autocompleteRef.current.addListener("place_changed", async () => {
      const place = autocompleteRef.current?.getPlace() as ExtendedPlaceResult
      if (place?.geometry?.location && place.place_id) {
        const placeId = place.place_id
        const latitude = place.geometry.location.lat()
        const longitude = place.geometry.location.lng()
        const addressComponents = place.address_components || []

        // Helper function to find address component by type
        const findComponent = (type: string) => {
          const component = addressComponents.find((c: AddressComponent) => c.types.includes(type))
          return component ? component.long_name : null
        }

        let timeZoneId: string | null = null
        // Access the API key using Vite's import.meta.env
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY

        if (!apiKey) {
          console.error("VITE_GOOGLE_MAPS_API_KEY is missing. Cannot fetch time zone.")
        } else {
          const fields = "timeZone"
          const placeDetailsUrl = `https://places.googleapis.com/v1/places/${placeId}?fields=${fields}&key=${apiKey}`

          try {
            const response = await fetch(placeDetailsUrl, {
              headers: {
                // Required header for field masking in Places API v1
                "X-Goog-FieldMask": fields,
              },
            })
            if (!response.ok) {
              throw new Error(`Failed to fetch time zone: ${response.statusText}`)
            }
            const data = await response.json()
            // Assuming the response structure is { timeZone: { id: "...", version: "..." } }
            timeZoneId = data?.timeZone?.id ?? null
            console.log(`Fetched IANA time zone ID: ${timeZoneId}`)
          } catch (error) {
            console.error("Error fetching place time zone details:", error)
            // timeZoneId remains null
          }
        }

        // Extract all address components
        const streetNumber = findComponent("street_number")
        const route = findComponent("route")
        const streetAddress = [streetNumber, route].filter(Boolean).join(" ")

        const addressData = {
          name: place.name,
          streetAddress,
          streetAddress2: null,
          city: findComponent("locality"),
          stateProvince: findComponent("administrative_area_level_1"),
          postalCode: findComponent("postal_code"),
          country: findComponent("country"),
          formattedAddress: place.formatted_address || "",
          placeId: place.place_id || "",
          administrativeAreaLevel1: findComponent("administrative_area_level_1"),
          administrativeAreaLevel2: findComponent("administrative_area_level_2"),
          neighborhood: findComponent("neighborhood"),
          websiteUrl: place.website,
          googleMapsUrl: place.url,
          phone: place.international_phone_number,
          timeZone: timeZoneId,
        }

        onLocationSelect(latitude, longitude, addressData)
      } else {
        // Handle cases where place_id or geometry might be missing
        console.warn("Place selected but missing geometry or place_id:", place)
      }
    })

    return () => {
      // Cleanup
      google.maps.event.clearInstanceListeners(autocompleteRef.current!)
    }
  }, [onLocationSelect, includeBusinesses])

  return (
    <Input
      ref={inputRef}
      type="text"
      placeholder={includeBusinesses ? "Search for a business" : "Enter an address"}
      className={className}
      data-1p-ignore
      autoComplete="off"
    />
  )
}
